import { PaymentApi } from "api";
import { PAYMENT_STATUSES, REFUND_STATUSES } from "consts/payments";

const RESERVING_STATUSES: (number | null)[] = [
  REFUND_STATUSES.Requesting,
  REFUND_STATUSES.Requested,
  REFUND_STATUSES.Completed,
];

export const REFUNDS_DISABLED_REASON = "Zwroty przez Autopay są wyłączone w ustawieniach.";

const toCents = (value: string | number): number => Math.round(Number(value) * 100);

// Refunds that already hold part of the payment: Rejected/Failed ones released it.
export function sumReservedRefunds(payment: PaymentApi): number {
  const cents = (payment.refunds ?? [])
    .filter((r) => RESERVING_STATUSES.includes(r.status))
    .reduce((sum, r) => sum + toCents(r.amount), 0);
  return cents / 100;
}

const sumByStatuses = (payment: PaymentApi, statuses: (number | null)[]): number =>
  (payment.refunds ?? []).filter((r) => statuses.includes(r.status)).reduce((sum, r) => sum + toCents(r.amount), 0) / 100;

// Refunds Autopay accepted (Requested + Completed) - what is really returned.
export const sumAcceptedRefunds = (payment: PaymentApi): number =>
  sumByStatuses(payment, [REFUND_STATUSES.Requested, REFUND_STATUSES.Completed]);

// Refunds still waiting for an Autopay answer.
export const sumRequestingRefunds = (payment: PaymentApi): number =>
  sumByStatuses(payment, [REFUND_STATUSES.Requesting]);

export type RefundAvailability = { canRefund: true; refundable: number } | { canRefund: false; reason: string };

// Reasons mirror the API validation messages, in the same order.
export function getRefundAvailability(payment: PaymentApi, refundsEnabled: boolean): RefundAvailability {
  if (!refundsEnabled) return { canRefund: false, reason: REFUNDS_DISABLED_REASON };
  if (payment.refundOfId != null) return { canRefund: false, reason: "Nie można zwrócić zwrotu." };
  if (payment.status !== PAYMENT_STATUSES.SUCCESS) {
    return { canRefund: false, reason: "Można zwrócić tylko opłaconą płatność." };
  }
  if (payment.isMultiCourse) {
    return { canRefund: false, reason: "Płatność za kilka kursów zwróć w panelu Autopay." };
  }
  if (payment.outsideSystem || !payment.remoteId) {
    return {
      canRefund: false,
      reason: "Tej płatności nie opłacono przez Autopay, więc nie da się jej zwrócić z systemu.",
    };
  }
  if ((payment.refunds ?? []).some((r) => r.status === REFUND_STATUSES.Requesting)) {
    return { canRefund: false, reason: "Poprzedni zwrot tej płatności czeka na odpowiedź Autopay. Użyj „Ponów”." };
  }
  const refundable = (toCents(payment.amount) - toCents(sumReservedRefunds(payment))) / 100;
  if (refundable <= 0) return { canRefund: false, reason: "Płatność została już w całości zwrócona." };
  return { canRefund: true, refundable };
}
